import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Send, Wrench, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../../../../components/ui/Modal';
import { LineItemsEditor, type EditableLineItem, type LineItemType } from '../../../../components/ui/LineItemsEditor';
import { supabase } from '../../../../lib/supabase';
import { errorMessage, reportError } from '../../../../lib/errors';
import { fitWithin } from '../../../../lib/images';
import { useOrgSettings } from '../../../../hooks/useOrgSettings';
import { additionalWorkPhotoPath, money, previewGross, type AdditionalWorkRequest } from './additionalWork';

/**
 * "+ Add extra work": price what was found, attach a photo, send it.
 *
 * Built for one hand and under a minute: the camera opens straight from the
 * first button, a line is already waiting to be filled in, and the primary
 * action is "Send for approval" (which saves first). "Save draft" is there for
 * the mechanic who wants the owner to price it.
 *
 * mode:
 *   new     a fresh request on the job
 *   edit    a draft not yet sent (fn_additional_work_save with its id)
 *   revise  a request already sent: the customer's link is replaced by a new
 *           one (fn_additional_work_revise), and the old link stops working
 */
interface Props {
    isOpen: boolean;
    onClose: () => void;
    onDone: () => void;
    jobId: string;
    orgId: string;
    /** The job's VAT rate as the server decided it (fn_get_job_financials). */
    vatRate: number;
    /**
     * The job's lines as they stand now (fn_get_job_authorisation
     * current_total) -- not the approved total, which stays at the prices the
     * customer agreed even after the job comes in under them.
     */
    currentTotal: number;
    mode: 'new' | 'edit' | 'revise';
    existing?: AdditionalWorkRequest | null;
}

interface Photo {
    path: string;
    preview: string | null;
    /** Uploaded in this session — removed from storage if dropped again. */
    fresh: boolean;
}

const MAX_PHOTOS = 6;
const PHOTO_MAX_PX = 1600;

const uid = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2);

/** Best-effort downscale to a JPEG; returns the original if the browser cannot decode it (e.g. HEIC on Android). */
async function shrinkPhoto(file: File): Promise<Blob> {
    try {
        const bitmap = await createImageBitmap(file);
        const { width, height } = fitWithin(bitmap.width, bitmap.height, PHOTO_MAX_PX);
        if (!width || (width === bitmap.width && file.size < 1_500_000)) return file;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d')?.drawImage(bitmap, 0, 0, width, height);
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
        return blob ?? file;
    } catch {
        return file;
    }
}

export function AdditionalWorkSheet({ isOpen, onClose, onDone, jobId, orgId, vatRate, currentTotal, mode, existing }: Props) {
    const { settings } = useOrgSettings();
    const partsMarkup = settings.default_parts_markup || 0;
    const labourRate = settings.default_labor_rate || 0;

    // The sheet mounts fresh for each open (AdditionalWorkPanel renders it only
    // while open, keyed by request), so the initial state is read from props.
    const [lines, setLines] = useState<EditableLineItem[]>(() => existing
        ? existing.line_items.map((l) => ({
            id: uid(), type: l.type, description: l.description,
            quantity: Number(l.quantity), unitPrice: Number(l.unitPrice), costPrice: Number(l.costPrice || 0),
        }))
        : [{ id: uid(), type: 'part', description: '', quantity: 1, unitPrice: 0, costPrice: 0 }]);
    const [notes, setNotes] = useState(existing?.notes || '');
    const [photos, setPhotos] = useState<Photo[]>(() =>
        (existing?.photos ?? []).map((path) => ({ path, preview: null, fresh: false })));
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState<null | 'save' | 'send'>(null);
    const fileInput = useRef<HTMLInputElement>(null);

    // Thumbnails for photos already on the request: the bucket is private.
    useEffect(() => {
        const paths = existing?.photos ?? [];
        if (!paths.length) return;
        let alive = true;
        supabase.storage.from('diagnostic-photos').createSignedUrls(paths, 900).then(({ data, error }) => {
            if (!alive) return;
            if (error) { reportError(error, 'additional-work-sheet.sign-photos'); return; }
            setPhotos((prev) => prev.map((p) => ({
                ...p,
                preview: p.preview ?? data?.find((d) => d.path === p.path)?.signedUrl ?? null,
            })));
        });
        return () => { alive = false; };
    }, [existing]);

    const sellPrice = (cost: number) => Number((cost * (1 + partsMarkup / 100)).toFixed(2));
    const addLine = (type: LineItemType) =>
        setLines((prev) => [...prev, { id: uid(), type, description: '', quantity: 1, unitPrice: type === 'labour' ? labourRate : 0, costPrice: 0 }]);
    const updateLine = (id: string, patch: Partial<EditableLineItem>) =>
        setLines((prev) => prev.map((l) => {
            if (l.id !== id) return l;
            const next = { ...l, ...patch };
            if (patch.costPrice !== undefined && next.type === 'part') next.unitPrice = sellPrice(patch.costPrice);
            return next;
        }));
    const removeLine = (id: string) => setLines((prev) => prev.filter((l) => l.id !== id));

    const onPickPhotos = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []).slice(0, MAX_PHOTOS - photos.length);
        e.target.value = '';
        if (!files.length) return;
        setUploading(true);
        try {
            for (const file of files) {
                const blob = await shrinkPhoto(file);
                const name = blob === file ? file.name : 'photo.jpg';
                const path = additionalWorkPhotoPath(orgId, jobId, name);
                const { error } = await supabase.storage
                    .from('diagnostic-photos')
                    .upload(path, blob, { contentType: blob.type || file.type, upsert: false });
                if (error) {
                    reportError(error, 'additional-work-sheet.upload-photo');
                    toast.error('A photo could not be uploaded.');
                    continue;
                }
                setPhotos((prev) => [...prev, { path, preview: URL.createObjectURL(blob), fresh: true }]);
            }
        } finally {
            setUploading(false);
        }
    };

    const removePhoto = async (photo: Photo) => {
        setPhotos((prev) => prev.filter((p) => p.path !== photo.path));
        // Only this session's uploads: an older photo may still be on a sent request.
        if (photo.fresh) {
            const { error } = await supabase.storage.from('diagnostic-photos').remove([photo.path]);
            if (error) reportError(error, 'additional-work-sheet.remove-photo');
        }
    };

    const cleanLines = lines
        .filter((l) => l.description.trim() || l.unitPrice > 0)
        .map((l) => ({
            type: l.type, description: l.description.trim(), quantity: l.quantity,
            unitPrice: l.unitPrice, costPrice: l.costPrice || 0,
        }));
    const extra = previewGross(cleanLines, vatRate);
    const canSubmit = cleanLines.length > 0 && cleanLines.every((l) => l.description && l.quantity > 0) && !uploading && !busy;

    /** Saves and returns the id of the draft to send. */
    const save = async (): Promise<string | null> => {
        const photoPaths = photos.map((p) => p.path);
        if (mode === 'revise' && existing) {
            const { data, error } = await supabase.rpc('fn_additional_work_revise', {
                p_quote_id: existing.id, p_line_items: cleanLines, p_notes: notes || null, p_photos: photoPaths,
            });
            if (error) { reportError(error, 'additional-work-sheet.fn-additional-work-revise'); toast.error(errorMessage(error)); return null; }
            return data as string;
        }
        const { data, error } = await supabase.rpc('fn_additional_work_save', {
            p_job_id: jobId, p_line_items: cleanLines, p_notes: notes || null, p_photos: photoPaths,
            p_quote_id: mode === 'edit' && existing ? existing.id : null,
        });
        if (error) { reportError(error, 'additional-work-sheet.fn-additional-work-save'); toast.error(errorMessage(error)); return null; }
        return data as string;
    };

    const onSaveDraft = async () => {
        setBusy('save');
        try {
            const id = await save();
            if (!id) return;
            toast.success(mode === 'revise' ? 'Revision saved as a draft — send it when ready.' : 'Saved as a draft.');
            onDone();
        } finally {
            setBusy(null);
        }
    };

    const onSend = async () => {
        setBusy('send');
        try {
            const id = await save();
            if (!id) return;
            const { data, error } = await supabase.functions.invoke('send-quote-email', { body: { quote_id: id } });
            if (error || (data as { error?: string } | null)?.error) {
                // Saved, but not sent: the draft stays on the job to send again.
                const msg = (data as { error?: string } | null)?.error || await functionErrorMessage(error);
                if (error) reportError(error, 'additional-work-sheet.send-quote-email');
                // The function says so itself when the email did go out.
                toast.error(msg.startsWith('The email went out') ? msg : `Saved as a draft, but not sent: ${msg}`);
                onDone();
                return;
            }
            toast.success('Sent to the customer for approval.');
            onDone();
        } finally {
            setBusy(null);
        }
    };

    const title = mode === 'new' ? 'Add extra work'
        : mode === 'edit' ? `Edit draft #${existing?.sequence_no ?? ''}`
        : `Revise extra work #${existing?.sequence_no ?? ''}`;

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => { if (!busy) onClose(); }}
            title={title}
            subtitle={mode === 'revise' ? 'The customer’s current link stops working and a new one is sent.' : 'Only work the customer accepts reaches the invoice.'}
            icon={Wrench}
            maxWidth="max-w-2xl"
            footer={
                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                    <button
                        type="button"
                        onClick={onSaveDraft}
                        disabled={!canSubmit}
                        className="fd-btn fd-btn-secondary"
                    >
                        {busy === 'save' ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save draft'}
                    </button>
                    <button
                        type="button"
                        onClick={onSend}
                        disabled={!canSubmit}
                        className="fd-btn fd-btn-go fd-btn-lg"
                    >
                        {busy === 'send' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Send for approval
                    </button>
                </div>
            }
        >
            <div className="space-y-5">
                {/* Evidence first: on a phone the camera is the fastest way in. */}
                <section>
                    <div className="flex items-center justify-between gap-3 mb-2">
                        <h3 className="text-fd-sm-tight uppercase tracking-[0.16em] text-fg-3 font-display font-semibold">Photos</h3>
                        <span className="text-fd-3xs text-fg-4">{photos.length}/{MAX_PHOTOS}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {photos.map((p) => (
                            <div key={p.path} className="relative w-20 h-20 rounded-lg overflow-hidden bg-card border border-line">
                                {p.preview
                                    ? <img src={p.preview} alt="Evidence" className="w-full h-full object-cover" />
                                    : <div className="w-full h-full grid place-items-center"><Loader2 className="w-4 h-4 animate-spin text-fg-4" /></div>}
                                <button
                                    type="button"
                                    onClick={() => removePhoto(p)}
                                    aria-label="Remove photo"
                                    className="absolute top-0 right-0 w-11 h-11 grid place-items-start justify-items-end p-1"
                                >
                                    <span className="w-6 h-6 rounded-full bg-scrim/70 grid place-items-center text-fg"><X className="w-3.5 h-3.5" /></span>
                                </button>
                            </div>
                        ))}
                        {photos.length < MAX_PHOTOS && (
                            <button
                                type="button"
                                onClick={() => fileInput.current?.click()}
                                disabled={uploading}
                                className="w-20 h-20 rounded-lg border border-dashed border-line text-fg-3 hover:text-fg flex flex-col items-center justify-center gap-1 text-fd-3xs font-semibold"
                            >
                                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                                {uploading ? 'Uploading' : 'Add photo'}
                            </button>
                        )}
                        <input
                            ref={fileInput}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                            capture="environment"
                            multiple
                            onChange={onPickPhotos}
                            className="hidden"
                        />
                    </div>
                </section>

                <section className="min-w-0">
                    <h3 className="mb-2 text-fd-sm-tight uppercase tracking-[0.16em] text-fg-3 font-display font-semibold">Extra work and price</h3>
                    <LineItemsEditor
                        items={lines}
                        onUpdate={updateLine}
                        onRemove={removeLine}
                        onAdd={addLine}
                        partsMarkup={partsMarkup}
                    />
                </section>

                <section>
                    <label htmlFor="aw-notes" className="block mb-2 text-fd-sm-tight uppercase tracking-[0.16em] text-fg-3 font-display font-semibold">
                        What you found <span className="normal-case tracking-normal font-normal text-fg-4">(the customer sees this)</span>
                    </label>
                    <textarea
                        id="aw-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        maxLength={2000}
                        rows={2}
                        placeholder="e.g. Front pads down to 2mm — needs doing before MOT"
                        className="w-full bg-canvas border border-line rounded-lg px-3 py-2 text-sm text-fg outline-none focus:border-gold resize-none"
                    />
                </section>

                <section className="rounded-xl border border-line bg-card p-4 space-y-1.5 text-sm">
                    <div className="flex justify-between gap-3">
                        <span className="text-fg-3">Extra work{vatRate > 0 ? ' (inc. VAT)' : ''}</span>
                        <span className="font-mono font-bold text-fg">{money(extra)}</span>
                    </div>
                    <div className="flex justify-between gap-3 text-xs">
                        <span className="text-fg-4">Job total now</span>
                        <span className="font-mono text-fg-3">{money(currentTotal)}</span>
                    </div>
                    <div className="flex justify-between gap-3 pt-1.5 border-t border-line">
                        <span className="text-fg-3">Job total if accepted</span>
                        <span className="font-mono font-bold text-gold">{money(currentTotal + extra)}</span>
                    </div>
                </section>
            </div>
        </Modal>
    );
}

/** supabase-js wraps a non-2xx function response; the body holds our message. */
async function functionErrorMessage(error: unknown): Promise<string> {
    const ctx = (error as { context?: Response } | null)?.context;
    if (ctx && typeof ctx.json === 'function') {
        try {
            const body = await ctx.clone().json();
            if (body?.error) return String(body.error);
        } catch { /* not JSON */ }
    }
    return errorMessage(error, 'Please try again.');
}
